// iconSearch.js
//
// Human-friendly search + label helpers for the Segment icon picker
// (SegmentSettings/children/SegmentIcon.jsx). Nemesis F3/F4: the picker's tiles
// are matched (and, for a11y, labelled) by a react-icons *code* displayName —
// `TbBrandTwitter`, `TbMoodHappy`, `TbDeviceLaptop`, `FiTrash2`. Two problems:
//   1. search only matched the raw code name, so `tb`/`brand` matched everything
//      and synonyms (`delete`->trash, `photo`->camera) failed;
//   2. a raw code name is a poor accessible label for a screen reader.
//
// Both helpers work for EVERY react-icons family (Tb/Fi/Fa/Si/...) so the legacy
// `faCategories` set benefits too. `iconSearchText` is a strict SUPERSET of the
// old behaviour — it still contains the raw lowercased displayName — so no query
// that matched before can stop matching (non-regressive).

// Two-letter react-icons library prefix (Tb, Fi, Fa, Si, Md, Io, Bs, ...), plus
// the "Brand" qualifier Tabler/Simple-Icons use for logo glyphs.
const PREFIX_RE = /^[A-Z][a-z]/;

/**
 * Turn a react-icons code name into human words:
 *   "TbBrandTwitter" -> "twitter"
 *   "TbMoodHappy"    -> "mood happy"
 *   "FiTrash2"       -> "trash 2"
 * Safe on any string; returns "" for a falsy/typeless input.
 */
export const humanizeIconName = (displayName) => {
  if (typeof displayName !== "string" || displayName.length === 0) return "";
  let name = displayName;
  // Drop the 2-letter library prefix (TbHome -> Home, FiTrash2 -> Trash2).
  if (PREFIX_RE.test(name)) name = name.slice(2);
  // Drop a leading "Brand" qualifier (BrandTwitter -> Twitter).
  if (name.startsWith("Brand")) name = name.slice(5);
  // Split camelCase / letter-number boundaries into words.
  const words = name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Za-z])([0-9])/g, "$1 $2")
    .trim()
    .toLowerCase();
  return words;
};

// Small synonym map so common intents find the right glyph. Keyed by the raw
// react-icons displayName. Values are space-joined extra search terms. Kept
// intentionally small — the humanized label already covers the obvious word.
export const ICON_SEARCH_ALIASES = {
  TbTrash: "delete bin remove",
  TbCamera: "photo picture",
  TbMoodHappy: "smile emoji face happy emoticon",
  TbDeviceLaptop: "computer pc",
  TbDeviceDesktop: "computer pc monitor",
  TbDeviceMobile: "phone smartphone",
  TbBell: "notification alert",
  TbSearch: "find magnify",
  TbHome: "house",
  TbHeart: "love like favorite",
  TbStar: "favorite bookmark",
  TbMessageCircle: "chat comment speech",
  TbBrandTwitter: "x social",
  TbBrandTiktok: "social",
  TbCurrencyDollar: "money usd cash",
  TbReportMoney: "money finance",
  TbCoin: "money cash",
  TbCar: "vehicle auto",
  TbPlane: "flight travel airplane",
  TbBulb: "idea light lamp",
  TbTrophy: "award prize win",
  TbBurger: "food hamburger meal fastfood",
  TbHorse: "animal pony",
  TbPaw: "animal pet",
  TbHeartbeat: "health pulse medical",
  TbActivity: "health pulse fitness",
  // Icon-picker-expand (2026-08-20, ISP-9999): "Person" typed nothing because
  // TbUser had no alias entry (only its own humanized label "user" matched).
  // These cover the common ways people search for a human/people glyph.
  TbUser: "person people human account profile",
  TbUsers: "person people human group team",
  TbUserCircle: "person people human account profile avatar",
  TbUserPlus: "person people add invite",
  TbUserX: "person people remove delete",
  TbUserCheck: "person people verified approve",
  TbUserSearch: "person people find",
  TbUserShield: "person people protect security",
  TbUserStar: "person people favorite vip",
  TbUserEdit: "person people rename",
  TbFriends: "people group team social",
  TbBabyCarriage: "baby person infant stroller",
  TbBabyBottle: "baby person infant feeding",
  TbGenderMale: "person male man gender",
  TbGenderFemale: "person female woman gender",
  TbMoodSmile: "face person happy emoji",
  TbMoodSad: "face person unhappy emoji",
  TbMoodWink: "face person emoji",
  TbMoodAngry: "face person emoji mad",
  TbHandStop: "hand person stop",
};

/**
 * The full searchable text for one icon component: its humanized label PLUS the
 * raw lowercased displayName (backward-compatible) PLUS any synonym aliases.
 * The picker matches user input via `.includes()` against this string.
 */
// T22.90 (Kelly 2026-10-10 video: typed "number", then "five" -> nothing):
// digit icons answer to the digit, its word, "number", "numeral" and "digit".
const DIGIT_WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine"];
const NUMBER_ICON_RE = /^Tb(Circle|Square)?Number([0-9])$/;
export const numberIconAliases = (displayName) => {
  const m = typeof displayName === "string" ? NUMBER_ICON_RE.exec(displayName) : null;
  if (!m) return "";
  const d = Number(m[2]);
  return `${d} ${DIGIT_WORDS[d]} number numeral digit count`;
};

export const iconSearchText = (displayName) => {
  const raw = typeof displayName === "string" ? displayName.toLowerCase() : "";
  const human = humanizeIconName(displayName);
  const alias = [ICON_SEARCH_ALIASES[displayName] || "", numberIconAliases(displayName)]
    .filter(Boolean)
    .join(" ");
  return `${human} ${raw} ${alias}`.trim();
};
